// The twelve formats as brand data: names, groups, gels and the icon drawings. Pure data (no React, no CSS) so
// FormatBadge.tsx, the build script and the brand sheet all draw the same icons.
// Icons: 24px grid, 1.75 stroke, round caps and joins, 2px inner margin, outlines only.
// Ids and names match product/core/src/formats (FORMAT_IDS, FORMAT_DEFS).

export const FORMAT_GROUPS = ["Marketing", "Stories", "Learning", "Social", "Personal", "Music"] as const;
export type FormatGroup = (typeof FORMAT_GROUPS)[number];

export type FormatId =
  | "ad"
  | "documentary"
  | "cartoon"
  | "short-film"
  | "math-explainer"
  | "explainer"
  | "short"
  | "music-video"
  | "slideshow"
  | "kinetic"
  | "podcast"
  | "demo";

export const FORMAT_BRAND: Record<FormatId, { name: string; short: string; group: FormatGroup }> = {
  ad: { name: "Ad", short: "Ad", group: "Marketing" },
  demo: { name: "Product demo", short: "Demo", group: "Marketing" },
  documentary: { name: "Animated documentary", short: "Documentary", group: "Stories" },
  cartoon: { name: "Cartoon or short story", short: "Cartoon", group: "Stories" },
  "short-film": { name: "Short film or trailer", short: "Short film", group: "Stories" },
  "math-explainer": { name: "Math and science explainer", short: "Math explainer", group: "Learning" },
  explainer: { name: "Character explainer", short: "Explainer", group: "Learning" },
  short: { name: "Faceless Short", short: "Short", group: "Social" },
  kinetic: { name: "Kinetic typography", short: "Kinetic type", group: "Social" },
  podcast: { name: "Podcast clip", short: "Podcast clip", group: "Social" },
  slideshow: { name: "Photo slideshow", short: "Slideshow", group: "Personal" },
  "music-video": { name: "Music video", short: "Music video", group: "Music" },
};

/** Display order: grouped, the way the type picker and the landing list them. */
export const FORMAT_ORDER: FormatId[] = ["ad", "demo", "documentary", "cartoon", "short-film", "math-explainer", "explainer", "short", "kinetic", "podcast", "slideshow", "music-video"];

/** Each group's gel: the hue, and the dark-theme / light-theme hex (tools/palette.py; tokens.css --cao-gel-*). */
export const GELS: Record<FormatGroup, { gel: string; hue: number; dark: string; light: string }> = {
  Marketing: { gel: "Coral", hue: 32, dark: "#FFA28F", light: "#9E4333" },
  Stories: { gel: "Violet", hue: 295, dark: "#C3AFFF", light: "#6952A2" },
  Learning: { gel: "Chalk", hue: 158, dark: "#79D4A1", light: "#007847" },
  Social: { gel: "Cyan", hue: 210, dark: "#50D2E6", light: "#00758A" },
  Personal: { gel: "Straw", hue: 100, dark: "#CFBF63", light: "#746300" },
  Music: { gel: "Magenta", hue: 345, dark: "#F39FCF", light: "#934273" },
};

export type IconEl =
  | ["path", { d: string }]
  | ["circle", { cx: number; cy: number; r: number }]
  | ["rect", { x: number; y: number; width: number; height: number; rx: number }];

export const FORMAT_ICONS: Record<FormatId, IconEl[]> = {
  // a megaphone
  ad: [
    ["path", { d: "M4 10v4a1 1 0 0 0 1 1h2.5L13 19V5L7.5 9H5a1 1 0 0 0-1 1Z" }],
    ["path", { d: "M16.5 9.5a3.5 3.5 0 0 1 0 5" }],
    ["path", { d: "M19 7a7 7 0 0 1 0 10" }],
  ],
  // a browser window and a pointer
  demo: [
    ["rect", { x: 3, y: 4, width: 18, height: 13, rx: 1.5 }],
    ["path", { d: "M3 7.75h18" }],
    ["path", { d: "M12.5 11.5l7 2.6-3 1.2-1.2 3Z" }],
  ],
  // an archival photograph with its caption line
  documentary: [
    ["rect", { x: 3.5, y: 3.5, width: 17, height: 12.5, rx: 1.5 }],
    ["path", { d: "M3.5 13.5l4.5-4.25 3.5 3.25 2.5-2.25 6.5 5.75" }],
    ["circle", { cx: 15.75, cy: 7.5, r: 1.25 }],
    ["path", { d: "M7 20.5h10" }],
  ],
  // an open storybook
  cartoon: [
    ["path", { d: "M12 6.5C10 5 7 4.5 3.5 5v13c3.5-.5 6.5 0 8.5 1.5 2-1.5 5-2 8.5-1.5V5C17 4.5 14 5 12 6.5Z" }],
    ["path", { d: "M12 6.5v13" }],
  ],
  // a clapperboard
  "short-film": [
    ["path", { d: "M3.5 10.5h17v8a1.5 1.5 0 0 1-1.5 1.5H5a1.5 1.5 0 0 1-1.5-1.5Z" }],
    ["path", { d: "M3.6 10.4 3 7.2l15.6-3.4.7 3.2Z" }],
    ["path", { d: "M8.2 6.2l2.1 3.1M13.2 5.1l2.1 3.1" }],
  ],
  // axes and a curve being drawn
  "math-explainer": [
    ["path", { d: "M4 3.5V20h16.5" }],
    ["path", { d: "M6.5 16.5C9 16.5 9.5 7 12.5 7S16 13 19.5 13" }],
  ],
  // a host and a speech bubble
  explainer: [
    ["circle", { cx: 8.5, cy: 10, r: 3 }],
    ["path", { d: "M3.5 20.5a5 5 0 0 1 10 0" }],
    ["path", { d: "M14 3.5h5.5A1.5 1.5 0 0 1 21 5v3.5A1.5 1.5 0 0 1 19.5 10H17l-2.5 2v-2H14a1.5 1.5 0 0 1-1.5-1.5V5A1.5 1.5 0 0 1 14 3.5Z" }],
  ],
  // a 9:16 screen with a play button
  short: [
    ["rect", { x: 7, y: 2.5, width: 10, height: 19, rx: 2 }],
    ["path", { d: "M10.75 9.5v5l4-2.5Z" }],
  ],
  // a letter in motion
  kinetic: [
    ["path", { d: "M4 19.5 9 4.5l5 15" }],
    ["path", { d: "M6 14h6" }],
    ["path", { d: "M16.5 8h4M17.5 12h3M16.5 16h4" }],
  ],
  // a studio microphone
  podcast: [
    ["rect", { x: 9, y: 3, width: 6, height: 11, rx: 3 }],
    ["path", { d: "M5.5 11a6.5 6.5 0 0 0 13 0" }],
    ["path", { d: "M12 17.5V21M9 21h6" }],
  ],
  // two photos, stacked
  slideshow: [
    ["path", { d: "M7 4.5h12.5A1.5 1.5 0 0 1 21 6v10" }],
    ["rect", { x: 3, y: 8, width: 14, height: 12, rx: 1.5 }],
    ["path", { d: "M3 17.5l4-3.75 3 2.75 2-1.75 5 4.25" }],
  ],
  // a pair of notes
  "music-video": [
    ["path", { d: "M9 18V6.5l10-2.5v11.5" }],
    ["circle", { cx: 7, cy: 18, r: 2 }],
    ["circle", { cx: 17, cy: 15.5, r: 2 }],
  ],
};

/** An icon as an SVG string (build script, brand sheet, data URIs). */
export function formatIconSvg(id: FormatId, color = "currentColor", size = 24, stroke = 1.75): string {
  const inner = FORMAT_ICONS[id]
    .map(([tag, a]) => `<${tag} ${Object.entries(a).map(([k, v]) => `${k}="${v}"`).join(" ")}/>`)
    .join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="${color}" stroke-width="${stroke}" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
}
